import { useQuery } from '@apollo/client';
import { useMemo } from 'react';
import { GetCuratedDealBuyerDiscountDocument } from '../../../graphql/query/curatedDealBuyerDiscount/GetCuratedDealBuyerDiscount.generated.tsx';
import type { CuratedDealBuyerDiscountApi } from '../../../types/curatedDealBuyerDiscount.ts';
import { useDemandPartnerOptionsFetch } from './useDemandPartnerOptionsFetch.ts';
import { useSeatOptionsFetch } from './useSeatOptionsFetch.ts';

export const useCuratedDealBuyerDiscountFetch = (curatedDealBuyerDiscountUid: string) => {
  const { data, error, loading } = useQuery(GetCuratedDealBuyerDiscountDocument, {
    nextFetchPolicy: 'network-only',
    skip: !curatedDealBuyerDiscountUid,
    variables: {
      uid: curatedDealBuyerDiscountUid,
    },
  });

  const curatedDealBuyerDiscount: CuratedDealBuyerDiscountApi | undefined = data?.curatedDealBuyerDiscount.at(0);
  const demandPartnerId = curatedDealBuyerDiscount?.demand_partner_id;

  const seatIds = useMemo(() => {
    if (!curatedDealBuyerDiscount?.seat_ids) {
      return '';
    }

    return Object.keys(curatedDealBuyerDiscount?.seat_ids).join(',');
  }, [curatedDealBuyerDiscount]);

  const {
    isError: isDemandPartnerOptionsError,
    isLoading: isDemandPartnerOptionsLoading,
    mapById: demandPartnerById,
  } = useDemandPartnerOptionsFetch(!demandPartnerId);

  const {
    isError: isSeatOptionsError,
    isLoading: isSeatOptionsLoading,
    list: seatList,
    mapById: seatById,
  } = useSeatOptionsFetch(!seatIds, { id__in: seatIds });

  const demandPartnerName =
    demandPartnerId && demandPartnerById ? (demandPartnerById[demandPartnerId]?.name ?? demandPartnerId) : null;

  return {
    curatedDealBuyerDiscount,
    demandPartnerName,
    isError: !!error || isDemandPartnerOptionsError || isSeatOptionsError,
    isLoading: loading || (!!demandPartnerId && isDemandPartnerOptionsLoading) || (!!seatIds && isSeatOptionsLoading),
    seatById,
    seatList,
  };
};
